<!-- RefundConfirmModal.vue
     Modal de confirmação para solicitação de estorno e downgrade para Free.
     ─────────────────────────────────────────────────────────────
     Casos de uso:
     - O usuário está dentro da janela de 7 dias pós-pagamento e não
       utilizou o serviço (criou textos/anotações). O backend confirmou
       elegibilidade via CheckRefundEligibilityAsync.
     - Ao confirmar, o backend solicita o estorno ao Mercado Pago e
       rebaixa a assinatura para o plano Free.

     A action de fato (chamar o backend) fica no componente pai —
     este modal só comunica intenção e emite "confirm" / "close".

     Visualmente segue o padrão dos outros modais de confirmação
     (CancelImmediateConfirmModal, PauseSubscriptionConfirmModal,
     ReactivateSubscriptionConfirmModal), com paleta laranja-âmbar
     que diferencia "transação financeira reversível" de "cancelamento
     destrutivo" (vermelho) e "ação positiva" (verde).
-->

<script setup>
import { onMounted, onBeforeUnmount, computed } from "vue";

const props = defineProps({
  isRefunding: {
    type: Boolean,
    default: false,
  },
  // Mensagem opcional vinda do backend com a janela de elegibilidade.
  // Ex.: "Você pode solicitar o estorno até 20/05/2026."
  eligibilityMessage: {
    type: String,
    default: null,
  },
  // <<< NOVO: valor que será estornado em reais (vem do backend).
  // Se null/undefined, a frase é montada sem o valor.
  refundAmount: {
    type: Number,
    default: null,
  },
  // <<< NOVO: método de pagamento usado na assinatura. Pode ser tipo
  // ("credit_card", "pix", "account_money", "ticket") ou bandeira
  // ("visa", "master", etc). Classificado abaixo.
  paymentMethod: {
    type: String,
    default: null,
  },
  // <<< NOVO (Mudança A): valor de credit_balance que será devolvido ao
  // saldo do usuário no estorno. Vem do backend (CheckRefundEligibilityAsync).
  // Quando null/0, a frase do crédito não é exibida.
  creditBalanceRefundAmount: {
    type: Number,
    default: null,
  },
});

const emit = defineEmits(["close", "confirm"]);

// Classifica paymentMethod em 4 categorias (espelha o helper do backend).
function classifyMethod(raw) {
  const pm = (raw ?? "").trim().toLowerCase();
  if (pm === "pix") return "pix";
  if (pm === "account_money") return "account_money";
  if (pm === "ticket" || pm === "boleto") return "boleto";
  // credit_card + bandeiras (visa, master, amex, elo, etc) → cartão.
  return "card";
}

// Texto curto descrevendo o destino do dinheiro.
const destinationLabel = computed(() => {
  switch (classifyMethod(props.paymentMethod)) {
    case "pix":
      return "sua conta bancária";
    case "account_money":
      return "sua conta Mercado Pago";
    case "boleto":
      return "sua conta Mercado Pago";
    default:
      return "seu cartão";
  }
});

// Helper de formatação em R$ — reutilizado para os dois valores.
function formatBrl(value) {
  if (value == null) return null;
  try {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  } catch {
    return `R$ ${Number(value).toFixed(2).replace(".", ",")}`;
  }
}

// Valor formatado em R$.
const formattedAmount = computed(() => formatBrl(props.refundAmount));

// Flag para escolher entre frase com valor ou frase de fallback no template.
const hasAmount = computed(() => formattedAmount.value != null);

// <<< NOVO (Mudança A): crédito devolvido ao saldo, formatado.
const formattedCreditBalance = computed(() =>
  formatBrl(props.creditBalanceRefundAmount)
);

// <<< NOVO (Mudança A): só exibe a frase do crédito quando há valor > 0.
const hasCreditBalance = computed(
  () =>
    props.creditBalanceRefundAmount != null &&
    props.creditBalanceRefundAmount > 0
);

function close() {
  if (props.isRefunding) return;
  emit("close");
}

function confirm() {
  if (props.isRefunding) return;
  emit("confirm");
}

function handleEsc(e) {
  if (e.key === "Escape") close();
}

onMounted(() => {
  document.addEventListener("keydown", handleEsc);
});

onBeforeUnmount(() => {
  document.removeEventListener("keydown", handleEsc);
});
</script>

<template>
  <transition name="modal-fade" appear>
    <div
      class="modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="refund-confirm-title"
      @click.self="close"
    >
      <div class="modal-panel modal-panel--compact">
        <div class="modal-panel__icon">
          <i class="fas fa-undo-alt"></i>
        </div>

        <h4 id="refund-confirm-title" class="modal-panel__title">
          Solicitar reembolso?
        </h4>

        <!-- <<< MUDANÇA: a frase do crédito devolvido ao saldo agora é
             continuação deste parágrafo (não mais uma badge separada),
             com o valor em <strong> no mesmo padrão do valor do estorno. -->
        <p class="modal-panel__body">
          <template v-if="hasAmount">
            Você está prestes a estornar
            <strong>{{ formattedAmount }}</strong>
            e cancelar sua assinatura. O valor voltará para
            <strong>{{ destinationLabel }}</strong>.
            <template v-if="hasCreditBalance">
              Além disso, <strong>{{ formattedCreditBalance }}</strong>
              de crédito utilizado será devolvido ao seu saldo na Litterae.
            </template>
          </template>
          <template v-else>
            Você está prestes a estornar o valor pago e cancelar sua
            assinatura. O valor voltará para
            <strong>{{ destinationLabel }}</strong>.
            <template v-if="hasCreditBalance">
              Além disso, <strong>{{ formattedCreditBalance }}</strong>
              de crédito utilizado será devolvido ao seu saldo na Litterae.
            </template>
          </template>
        </p>

        <p class="modal-panel__body modal-panel__body--note">
          Após a confirmação, seu plano será rebaixado para
          <strong>Free</strong>. Esta ação não pode ser desfeita.
        </p>

        <p
          v-if="eligibilityMessage"
          class="modal-panel__body modal-panel__body--deadline"
        >
          <i class="fas fa-clock"></i>
          {{ eligibilityMessage }}
        </p>

        <div class="modal-panel__actions">
          <button
            type="button"
            class="btn btn-secondary"
            :disabled="isRefunding"
            @click="close"
          >
            Voltar
          </button>
          <button
            type="button"
            class="btn btn-refund"
            :disabled="isRefunding"
            @click="confirm"
          >
            <span v-if="isRefunding" class="btn-spinner"></span>
            <span>{{
              isRefunding ? "Processando..." : "Confirmar reembolso"
            }}</span>
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<style scoped>
.modal-panel__icon {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: #fff7ed;
  border: 1px solid #fed7aa;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ea580c;
  font-size: 1.3rem;
  margin-bottom: 0.25rem;
}

.modal-panel__title {
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--text-dark);
  margin: 0;
}

.modal-panel__body {
  font-size: 0.9rem;
  color: var(--text-light);
  line-height: 1.5;
  margin: 0;
  /* <<< MUDANÇA: parágrafos justificados. */
  text-align: justify;
}

/* Nota secundária sobre o downgrade — tom mais leve que o corpo principal. */
.modal-panel__body--note {
  font-size: 0.82rem;
  color: var(--text-subtle);
}

/* Caixinha amarela destacando a janela de elegibilidade. */
.modal-panel__body--deadline {
  font-size: 0.82rem;
  background: var(--warning-bg);
  border: 1px solid #facc15;
  color: var(--warning-text);
  border-radius: 8px;
  padding: 0.45rem 0.7rem;
  line-height: 1.45;
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.modal-panel__actions {
  display: flex;
  gap: 0.65rem;
  margin-top: 0.5rem;
}

.btn-refund {
  background-color: #fff7ed;
  color: #9a3412;
  border-color: #fed7aa;
}

.btn-refund:hover:not(:disabled) {
  background-color: #ffedd5;
}

.btn-spinner {
  display: inline-block;
  width: 13px;
  height: 13px;
  border: 2px solid #fed7aa;
  border-top-color: #9a3412;
  border-radius: 50%;
  animation: btn-spin 0.7s linear infinite;
  flex-shrink: 0;
}

@keyframes btn-spin {
  to {
    transform: rotate(360deg);
  }
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.25s ease;
}

.modal-fade-enter-active .modal-panel,
.modal-fade-leave-active .modal-panel {
  transition: transform 0.25s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-from .modal-panel,
.modal-fade-leave-to .modal-panel {
  transform: scale(0.96) translateY(-8px);
}

</style>